import { useQuery } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { api } from '../api/client'
import type { SalesPersonRow, SalesTeamOut } from '../api/types'
import { PageHeader } from '../components/Layout'
import { FilterBar } from '../components/FilterBar'
import { Kpi } from '../components/Kpi'
import { SearchBox } from '../components/SearchBox'
import { Panel } from '../components/States'
import { AXIS, ChartTip, GRID } from '../components/Charts'
import { useFilters, useParam } from '../lib/filters'
import { personKey } from '../lib/salesPerson'
import { branchName, count, formatDate, money, moneyCompact, num, percent } from '../lib/format'

const SORTS = ['sales', 'invoices', 'units', 'avg_sale', 'customers'] as const
type Sort = (typeof SORTS)[number]

/**
 * One control, one measure. Picking a measure re-ranks the chart *and* sorts
 * the table by the same figure — a segmented control sitting above a chart
 * that ignored it read as broken.
 */
interface Measure {
  label: string
  field: keyof SalesPersonRow
  isMoney: boolean
}

const MEASURES: Record<Sort, Measure> = {
  sales: { label: 'Amount sold', field: 'sales_ex_tax', isMoney: true },
  invoices: { label: 'Invoices', field: 'invoice_count', isMoney: false },
  units: { label: 'Items sold', field: 'units', isMoney: false },
  avg_sale: { label: 'Average sale', field: 'avg_sale', isMoney: true },
  customers: { label: 'Customers', field: 'distinct_customers', isMoney: false },
}

export function SalesTeamPage() {
  const { filters } = useFilters()
  const [sort, setSort] = useParam<Sort>('sort', 'sales', SORTS)
  const [params, setParams] = useSearchParams()
  const search = params.get('q') ?? ''

  function setSearch(next: string) {
    const updated = new URLSearchParams(params)
    if (next) updated.set('q', next)
    else updated.delete('q')
    setParams(updated, { replace: true })
  }

  const query = useQuery({
    queryKey: ['sales-team', filters.from, filters.to, filters.outletId, filters.scope, sort, search],
    queryFn: () =>
      api.get<SalesTeamOut>('/analytics/salespeople', {
        from_date: filters.from,
        to_date: filters.to,
        outlet_id: filters.outletId,
        scope: filters.scope,
        sort,
        search: search || null,
      }),
  })

  const rows = query.data?.items ?? []
  const summary = query.data?.summary

  // The chart is a ranking, so it only ever shows the leaders — past ten
  // people the bars are too short to compare and the table says it better.
  // The API already returns rows sorted by the chosen measure, so the leaders
  // are just the first ten.
  const measure = MEASURES[sort]
  const chartRows = rows
    .slice(0, 10)
    .map((row) => ({ name: row.name, value: num(row[measure.field] as never) }))

  const formatMeasure = (value: number) => (measure.isMoney ? money(value) : count(value))

  return (
    <>
      <PageHeader
        title="Sales team"
        subtitle={`${formatDate(filters.from)} – ${formatDate(filters.to)}`}
      />

      <div className="content stack">
        <FilterBar />

        {summary ? (
          <div className="grid cols-4">
            <Kpi
              label="People selling"
              value={count(summary.people)}
              foot={`top seller took ${percent(summary.top_share_pct)}`}
            />
            <Kpi
              label="Amount sold"
              value={money(summary.sales_ex_tax)}
              foot="ex. tax, net of refunds"
            />
            <Kpi label="Invoices" value={count(summary.invoice_count)} />
            <Kpi label="Items sold" value={count(summary.units)} />
            <Kpi label="Average per person" value={money(summary.avg_sales_per_person)} />
          </div>
        ) : null}

        <div className="row" style={{ alignItems: 'flex-end' }}>
          <SearchBox
            id="salesperson-search"
            label="Search the team"
            placeholder="Name — e.g. karen"
            value={search}
            onChange={setSearch}
          />
        </div>

        <div className="field">
          <label>Rank by</label>
          <div className="seg">
            {SORTS.map((option) => (
              <button
                key={option}
                className={sort === option ? 'on' : ''}
                onClick={() => setSort(option)}
              >
                {MEASURES[option].label}
              </button>
            ))}
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h2>{measure.label}</h2>
            <span className="hint">
              {chartRows.length < rows.length ? `top ${chartRows.length} of ` : ''}
              {count(rows.length)} people
            </span>
          </div>
          <div className="card-body">
            <Panel
              isLoading={query.isPending}
              error={query.error}
              isEmpty={chartRows.length === 0}
              emptyText="Nobody sold anything in this period."
            >
              <div className="chart tall">
                <ResponsiveContainer>
                  <BarChart
                    data={chartRows}
                    layout="vertical"
                    margin={{ top: 4, right: 16, bottom: 0, left: 8 }}
                  >
                    <CartesianGrid {...GRID} horizontal={false} />
                    <XAxis
                      type="number"
                      {...AXIS}
                      tickFormatter={(value) =>
                        measure.isMoney ? moneyCompact(value) : count(value)
                      }
                    />
                    <YAxis type="category" dataKey="name" {...AXIS} width={110} />
                    <Tooltip
                      content={<ChartTip format={(_, value) => formatMeasure(value)} />}
                      cursor={{ fill: 'var(--page)' }}
                    />
                    <Bar
                      dataKey="value"
                      name={measure.label}
                      fill="var(--brand)"
                      radius={[0, 3, 3, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Panel>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h2>Performance</h2>
            <span className="hint">
              {count(query.data?.total)} people{search ? ` matching “${search}”` : ''}
            </span>
          </div>
          <div className="card-body flush">
            <Panel
              isLoading={query.isPending}
              error={query.error}
              isEmpty={rows.length === 0}
              emptyText="Nobody sold anything in this period."
            >
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Salesperson</th>
                      <th className="num">Amount sold</th>
                      <th style={{ width: 140 }}>Share</th>
                      <th className="num">Invoices</th>
                      <th className="num">Items</th>
                      <th className="num">Average sale</th>
                      <th className="num">Items / sale</th>
                      <th className="num">Customers</th>
                      <th className="num">Discounts</th>
                      <th className="num">Refunds</th>
                      <th className="num">Last sale</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={personKey(row)}>
                        <td>
                          <Link to={`/sales-team/${personKey(row)}`}>{row.name}</Link>
                          <div className="cell-sub">
                            {row.outlet_names.map(branchName).join(', ') || '—'}
                            {row.sales_person_id === null ? ' · no HIKE staff id' : ''}
                          </div>
                        </td>
                        <td className="num strong">{money(row.sales_ex_tax)}</td>
                        <td>
                          <div className="row" style={{ gap: 8 }}>
                            <div className="bar" style={{ flex: 1 }}>
                              <span style={{ width: `${Math.min(100, row.share_pct)}%` }} />
                            </div>
                            <span className="cell-sub" style={{ width: 42, textAlign: 'right' }}>
                              {percent(row.share_pct)}
                            </span>
                          </div>
                        </td>
                        <td className="num">{count(row.invoice_count)}</td>
                        <td className="num">{count(row.units)}</td>
                        <td className="num">{money(row.avg_sale)}</td>
                        <td className="num">
                          {row.avg_basket_units === null ? '—' : row.avg_basket_units.toFixed(1)}
                        </td>
                        <td className="num">{count(row.distinct_customers)}</td>
                        <td className="num cell-sub">{money(row.discount_total)}</td>
                        <td className="num cell-sub">
                          {row.refund_count === 0 ? '—' : money(row.refunds_total)}
                        </td>
                        <td className="num cell-sub">{formatDate(row.last_sale)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>
          </div>
        </div>

        <p className="cell-sub">
          Amount sold is ex-tax and net of refunds — the same figure the dashboard reports, so
          the two pages agree. It is taken before discounts, which are listed separately;{' '}
          <Link to="/commission">commission</Link> deducts them, so a person's commission base is
          the smaller number.
        </p>
      </div>
    </>
  )
}
